import { DailyDiscountStats, DailyMarketStats, StayInputOverrides } from './types';

/** sell_plus_sur = base_sell + stay_surcharge (or 0 on a blackout date), then FX-converted per currency. */
function calcSellDisplay(
  baseSellPriceAud: number,
  stayNightlySurcharge: number,
  currencyCode: string,
  fxRateAudToNative: number,
  isBlackout: boolean,
): number {
  if (isBlackout) {
    return 0;
  }
  const sellPlusSur = baseSellPriceAud + stayNightlySurcharge;
  return currencyCode === 'AUD' ? sellPlusSur : sellPlusSur * fxRateAudToNative;
}

/** prv_plus_min = LOW + prv_inclusions. Blank if LOW is blank/0. */
function calcPrvPlusMin(
  low: number | null,
  prvInclusionsAud: number,
  prvInclusionsNative: number | null,
  currencyCode: string,
): number | null {
  if (low == null || low === 0) {
    return null;
  }
  const inclusions = currencyCode === 'AUD' ? prvInclusionsAud : (prvInclusionsNative ?? prvInclusionsAud);
  return low + inclusions;
}

/** Ports the mockup's `calcPRV`: (prvPlusMin - sellDisplay) / prvPlusMin * 100. */
function calcGenDiscountPct(prvPlusMin: number | null, sellDisplay: number | null): number | null {
  // Per the Confluence spec: skip the row if LOW/prvPlusMin is blank/0 OR sellDisplay is 0 (blackout).
  if (prvPlusMin == null || prvPlusMin === 0 || sellDisplay == null || sellDisplay === 0) {
    return null;
  }
  return ((prvPlusMin - sellDisplay) / prvPlusMin) * 100;
}

function calcSellVsMinOtaPct(low: number | null, sellDisplay: number | null): number | null {
  if (low == null || low === 0 || sellDisplay == null || sellDisplay === 0) {
    return null;
  }
  return ((low - sellDisplay) / low) * 100;
}

/**
 * Per-date genuine-discount / sell-vs-min-OTA figures, per the Confluence "OTA Rates" spec.
 * Positive percentages mean the sell price is cheaper than the OTA benchmark.
 */
export function computeDailyDiscountStats(
  marketStats: Array<DailyMarketStats>,
  inputs: StayInputOverrides,
): Array<DailyDiscountStats> {
  const surchargeByDate = inputs.stayNightlySurchargeByDate ?? {};
  const blackoutDates = inputs.blackoutDates ?? new Set<string>();

  return marketStats.map((stats) => {
    const isBlackout = blackoutDates.has(stats.date);
    const sellDisplay = calcSellDisplay(
      inputs.baseSellPriceAud,
      surchargeByDate[stats.date] ?? 0,
      inputs.currencyCode,
      inputs.fxRateAudToNative,
      isBlackout,
    );
    const prvPlusMin = calcPrvPlusMin(
      stats.low,
      inputs.prvInclusionsAud,
      inputs.prvInclusionsNative,
      inputs.currencyCode,
    );

    return {
      date: stats.date,
      low: stats.low,
      prvPlusMin,
      sellDisplay,
      genDiscountPct: calcGenDiscountPct(prvPlusMin, sellDisplay),
      sellVsMinOtaPct: calcSellVsMinOtaPct(stats.low, sellDisplay),
    };
  });
}
